/**
 * AR-6 Evaluation Level Mapping
 * Method ID: b1-evaluation-level
 *
 * Maps a 0-100 score to one of five evaluation levels (Excellent / Good /
 * Average / BelowAverage / Poor), and computes the overall turn score from
 * its sub-scores via a fixed weighted combination.
 *
 * 将 0-100 分映射为五档评估等级（优秀/良好/中等/偏弱/较差），
 * 并按固定权重由各子分合成弯综合评分。
 *
 * Deterministic per PO-5.4 (no random numbers).
 * 按 PO-5.4 确定性实现（无随机数）。
 */

import { EvaluationLevel } from '../types/enums';
import { AR6_THRESHOLDS } from '../constants/algorithm-params';

// ===== Score → Level Mapping =====

/**
 * Map a 0-100 score to an EvaluationLevel.
 *
 * 分数到等级的映射：
 *   >= 90  → Excellent (优秀)
 *   75-89  → Good (良好)
 *   60-74  → Average (中等)
 *   40-59  → BelowAverage (偏弱)
 *   < 40   → Poor (较差)
 *
 * @param score - 0-100 score / 0-100 分
 * @returns EvaluationLevel / 评估等级
 */
export function mapScoreToLevel(score: number): EvaluationLevel {
  if (score >= AR6_THRESHOLDS.excellent) return EvaluationLevel.Excellent;        // >= 90
  if (score >= AR6_THRESHOLDS.good) return EvaluationLevel.Good;                  // 75-89
  if (score >= AR6_THRESHOLDS.average) return EvaluationLevel.Average;            // 60-74
  if (score >= AR6_THRESHOLDS.belowAverage) return EvaluationLevel.BelowAverage;  // 40-59
  return EvaluationLevel.Poor;                                                     // < 40
}

// ===== Overall Turn Score =====

/**
 * Input sub-scores for the overall turn score.
 *
 * 弯综合评分的子分输入。每项均为 0-100。
 */
export interface OverallScoreInput {
  /** AR-5 roundness + symmetry combined (0-100) / AR-5 圆润度+对称度合成 */
  turnShapeScore: number;
  /** AR-4 edging synchronization (0-100) / AR-4 立刃同步率 */
  edgingSyncScore: number;
  /** AR-3 outside pressure ratio (0-100) / AR-3 外板压力占比 */
  outsidePressureScore: number;
  /** AR-2 run-level or per-turn classification (0-100) / AR-2 分类得分 */
  turnClassificationScore: number;
}

/**
 * Fixed weights for combining sub-scores into the overall turn score.
 *
 * 弯综合评分的固定权重：
 *   turnShape       0.30
 *   edgingSync      0.20
 *   outsidePressure 0.25
 *   classification  0.25
 */
const OVERALL_WEIGHTS = {
  shape: 0.30,
  sync: 0.20,
  pressure: 0.25,
  classification: 0.25,
} as const;

/**
 * Compute the weighted overall turn score from its sub-scores.
 *
 * The result is clamped to [0, 100].
 *
 * 按固定权重合成弯综合评分，结果钳制到 [0, 100]。
 *
 * @param input - sub-scores / 子分输入
 * @returns overall turn score in [0, 100] / 弯综合评分
 */
export function computeOverallTurnScore(input: OverallScoreInput): number {
  const score =
    input.turnShapeScore * OVERALL_WEIGHTS.shape +
    input.edgingSyncScore * OVERALL_WEIGHTS.sync +
    input.outsidePressureScore * OVERALL_WEIGHTS.pressure +
    input.turnClassificationScore * OVERALL_WEIGHTS.classification;
  return Math.max(0, Math.min(100, score));
}

// ===== Level → Label / Color =====

/**
 * Map an EvaluationLevel to its Chinese label.
 *
 * 等级到中文标签的映射。
 *
 * @param level - EvaluationLevel / 评估等级
 * @returns Chinese label / 中文标签
 */
export function levelToLabel(level: EvaluationLevel): string {
  switch (level) {
    case EvaluationLevel.Excellent:
      return '优秀';
    case EvaluationLevel.Good:
      return '良好';
    case EvaluationLevel.Average:
      return '中等';
    case EvaluationLevel.BelowAverage:
      return '偏弱';
    case EvaluationLevel.Poor:
      return '较差';
  }
}

/**
 * Map an EvaluationLevel to a coarse status color key.
 *
 *   Excellent / Good       → 'good'
 *   Average                → 'ok'
 *   BelowAverage / Poor    → 'bad'
 *
 * 等级到状态色键的映射（优秀/良好→good，中等→ok，偏弱/较差→bad）。
 *
 * @param level - EvaluationLevel / 评估等级
 * @returns status color key / 状态色键
 */
export function levelToStatusColor(
  level: EvaluationLevel
): 'good' | 'ok' | 'bad' {
  switch (level) {
    case EvaluationLevel.Excellent:
    case EvaluationLevel.Good:
      return 'good';
    case EvaluationLevel.Average:
      return 'ok';
    case EvaluationLevel.BelowAverage:
    case EvaluationLevel.Poor:
      return 'bad';
  }
}

// ===== Six-Dimension Score (FR-25 Radar Chart) =====

/**
 * Input sub-scores for the six-dimension radar score (FR-25).
 * Each dimension is 0-100.
 *
 * 六维评估（FR-25 雷达图）的子分输入，每维 0-100。
 */
export interface SixDimScoreInput {
  /** Left/right balance / 左右平衡 */
  balance: number;
  /** Edging depth / 立刃深度 */
  edging: number;
  /** Kinematics (speed, g-force) / 运动学（速度、g 力） */
  kinematics: number;
  /** Pressure control / 压力控制 */
  pressure: number;
  /** Rotation control / 旋转控制 */
  rotation: number;
}

/**
 * Fixed weights for the six-dimension overall score.
 *
 * 六维综合得分的固定权重：
 *   balance     0.15
 *   edging      0.25
 *   kinematics  0.15
 *   pressure    0.25
 *   rotation    0.20
 */
const SIX_DIM_WEIGHTS = {
  balance: 0.15,
  edging: 0.25,
  kinematics: 0.15,
  pressure: 0.25,
  rotation: 0.20,
} as const;

/**
 * Compute the six-dimension overall score for the FR-25 radar chart.
 *
 * The result is clamped to [0, 100].
 *
 * 计算六维评估综合得分（FR-25 雷达图），结果钳制到 [0, 100]。
 *
 * @param input - six dimension sub-scores / 六维子分
 * @returns overall score in [0, 100] / 综合得分
 */
export function computeSixDimScore(input: SixDimScoreInput): number {
  const score =
    input.balance * SIX_DIM_WEIGHTS.balance +
    input.edging * SIX_DIM_WEIGHTS.edging +
    input.kinematics * SIX_DIM_WEIGHTS.kinematics +
    input.pressure * SIX_DIM_WEIGHTS.pressure +
    input.rotation * SIX_DIM_WEIGHTS.rotation;
  return Math.max(0, Math.min(100, score));
}
